import { useCallback, useEffect, useState } from 'react';

import { ApiError } from './client';

export interface AsyncState<T> {
  data: T | undefined;
  loading: boolean;
  error: ApiError | undefined;
  /** Re-runs the request; useful for a retry button on the error state. */
  reload: () => void;
}

interface RequestState<T> {
  data: T | undefined;
  loading: boolean;
  error: ApiError | undefined;
}

function toApiError(cause: unknown): ApiError {
  return cause instanceof ApiError
    ? cause
    : new ApiError(cause instanceof Error ? cause.message : 'Request failed', 'UNKNOWN_ERROR', 0);
}

/**
 * Runs an API call and tracks loading/error state. There is no data-fetching
 * library in this app, so this is the one place that logic lives rather than
 * being repeated per page.
 *
 * `deps` controls when the request re-runs, exactly like `useEffect`. The
 * fetcher itself is intentionally not a dependency: an inline arrow would be a
 * new function on every render and re-fetch forever.
 */
export function useApi<T>(fetcher: () => Promise<T>, deps: readonly unknown[] = []): AsyncState<T> {
  // One state object rather than three: separate setters would each queue their
  // own render, and a half-applied update would briefly report both loading and
  // a stale error at once.
  const [state, setState] = useState<RequestState<T>>({
    data: undefined,
    loading: true,
    error: undefined,
  });
  const [reloadToken, setReloadToken] = useState(0);

  // Which request the current state belongs to. Comparing it during render —
  // rather than calling setState at the top of the effect — is what keeps a
  // dependency change from costing an extra render pass before the fetch starts.
  const requestKey = JSON.stringify([deps, reloadToken]);
  const [settledKey, setSettledKey] = useState(requestKey);

  if (settledKey !== requestKey) {
    setSettledKey(requestKey);
    setState((current) => ({ ...current, loading: true, error: undefined }));
  }

  const reload = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  useEffect(() => {
    // A slow first request that resolves after the user has navigated away would
    // otherwise set state on an unmounted component, and a fast second request
    // could be overwritten by a slow first one.
    let active = true;

    fetcher()
      .then((result) => {
        if (active) {
          setState({ data: result, loading: false, error: undefined });
        }
      })
      .catch((cause: unknown) => {
        if (active) {
          setState({ data: undefined, loading: false, error: toApiError(cause) });
        }
      });

    return (): void => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, reloadToken]);

  return { ...state, reload };
}
